import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ILocation, IVehicle } from '@boma/supplier-portal/shared/entities';
import {
  HelperFunctionsService,
  LocationsService,
  VehiclesService,
} from '@boma/supplier-portal/shared/services';
import {
  LocationsFacade,
  VehiclesFacade,
  getSelectedSupplierId,
  loadLocations,
  loadVehicles,
  setLocationQuery,
} from '@boma/supplier-portal/shared/state';
import { Store } from '@ngrx/store';
import { Calendar } from 'primeng/calendar';
import { MultiSelect } from 'primeng/multiselect';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'boma-location-date-range-selector',
  templateUrl: './location-date-range-selector.component.html',
  styleUrls: ['./location-date-range-selector.component.scss'],
})
export class LocationDateRangeSelectorComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  locationSearchResults: ILocation | IVehicle | any = []; // any in here to fix the filter issue
  selectedLocation: ILocation | IVehicle;
  entityId: string;
  entityIdSubscription$: Subscription;
  today: Date = new Date();
  startDate: Date = null;
  endDate: Date = new Date(new Date().setHours(23, 59, 59, 999));
  selectedChip: any = null;
  showGlobalSearch = false;
  searchGlobal = false;
  isMonday = this.today.getDay() === 1;
  isStartOfMonth = this.today.getDate() === 1;

  locationFilteredResults: ILocation | IVehicle | any = [];

  locationSearchSubscription$: Subscription;

  locationSearchSubject = new Subject<string>();

  @ViewChild('multiSelect') multiSelect: MultiSelect;

  @ViewChild('calendarEnd') calendarEnd: Calendar;
  @ViewChild('calendarStart') calendarStart: Calendar;
  selecting = false;

  // To prevent errors on pages that currently use this
  @Input() legacy = true;
  @Input() simpleDropDown = false; // Single drop down with preset dates and custom calendar pick
  @Input() initialLocationId: string | null;
  @Input() emitDateImmediately = false;
  @Input() dateRequired = true;
  @Input() requiredPresets: string[] = [];
  @Input() locationRequired = true;
  @Input() locationType = 'All';
  @Input() changeType = true;
  @Input() showCards = false;
  @Input() showLabels = true;
  @Input() singleDay = false;
  @Input() singleLocation = true;
  @Input() placeHolderText = 'All Locations';
  @Input() placeHolderDateText = { from: 'Start Date', to: 'End Date' };
  @Input() excludeLocationId: string;
  @Input() selectedLocations: any[] = [];
  @Input() shortcutDate = null;
  @Input() showTime = false;
  @Input() forceEndOfDay = false;
  @Input() selectedDates: { fromDate: string; toDate: string } = {
    fromDate: null,
    toDate: null,
  };
  @Output() locationSelectedChange = new EventEmitter();
  @Output() datesSelected = new EventEmitter();

  wasSimpleDropDown = false;
  locationTypeOption = 'All';
  locationTypeOptions = [
    {
      name: 'Locations & Vehicles',
      value: 'All',
    },
    {
      name: 'Locations',
      value: 'Location',
    },
    {
      name: 'Vehicles',
      value: 'Vehicle',
    },
  ];

  chips = [
    {
      label: 'Today',
      value: 0,
      visible: true,
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
      visible: true,
    },
    {
      label: 'This Week',
      value: 'startOfWeek',
      visible: !this.isMonday,
    },
    {
      label: 'Last Week',
      value: 'startOfLastWeek',
      visible: true,
    },
    {
      label: 'Last 7 Days',
      value: -7,
      visible: this.requiredPresets.length
        ? !!this.requiredPresets.indexOf('Last 7 Days')
        : true,
    },
    {
      label: 'Last 30 Days',
      value: -30,
      visible: this.requiredPresets.length
        ? !!this.requiredPresets.indexOf('Last 30 Days')
        : true,
    },
  ];

  chipsMonth = [
    {
      label: 'This Month',
      value: 'startOfMonth',
      visible: !this.isStartOfMonth,
    },
    {
      label: 'Last Month',
      value: 'lastMonth',
      visible: this.requiredPresets.length
        ? !!this.requiredPresets.indexOf('Last Month')
        : true,
    },
    // {
    //   label: 'Three Months',
    //   value: 'threeMonths',
    //   visible: this.requiredPresets.length
    //     ? !!this.requiredPresets.indexOf('Three Months')
    //     : false,
    // },
    // {
    //   label: 'Year To Date',
    //   value: 'yearToDate',
    //   visible: this.requiredPresets.length
    //     ? !!this.requiredPresets.indexOf('Year To Date')
    //     : false,
    // },
    // {
    //   label: 'All Time',
    //   value: null,
    //   visible: this.requiredPresets.length
    //     ? !!this.requiredPresets.indexOf('All Time')
    //     : true,
    // },
  ];

  selectedDate: any;

  locationElement: any;

  locations$: Observable<ILocation[]>;
  vehicles$: Observable<IVehicle[]>;

  filterValue = '';

  allVehiclesAndLocations: (IVehicle | ILocation)[];

  locationVehicleSubscription$: Subscription;

  loadingLocations = true;

  multiSelectContainer;

  currentPage = '1';

  isMobile;

  constructor(
    private locationsFacade: LocationsFacade,
    private locationService: LocationsService,
    private vehiclesFacade: VehiclesFacade,
    private vehicleService: VehiclesService,
    private store: Store,
    private helperService: HelperFunctionsService
  ) {
    this.isMobile = this.helperService.isMobileDevice();
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log(changes);

    if (changes.forceEndOfDay) {
      this.handleDateSelection();
    }
    if (changes.excludeLocationId) {
      // We have to get the original data set in order to avoid missing previously filtered locations
      this.updateFilters();
    }
  }

  ngOnInit(): void {
    if (this.dateRequired) {
      if (this.selectedDates) {
        if (this.selectedDates.toDate)
          this.endDate = new Date(this.selectedDates.toDate);
        if (this.selectedDates.fromDate)
          this.startDate = new Date(this.selectedDates.fromDate);
      }

      // TODO DISABLING THIS FOR NOW WE SHOULD LET THE USER EXPLICITY SAVE THE DATE IF THEY WANT TO SHORTCUT IT
      // TODO ADD A KEEP DATE CHECKBOX SO IT STORES THAT IN MEMORY AND USES IT TILL TURNED OFF
      // const savedShortcut = localStorage.getItem('dateShortcut');
      const savedShortcut = null;

      if (
        (this.shortcutDate || savedShortcut) &&
        !this.selectedDates.fromDate
      ) {
        this.chips.map((x) => {
          if (x.label.toLowerCase() === this.shortcutDate.toLowerCase()) {
            this.selectedChip = savedShortcut || x.value; // we let the users preference take president here
          }
        });
        console.log(this.selectedChip);
        this.dateShortcut(this.selectedChip);
      }

      if (this.startDate && this.endDate) {
        //set hours to start
        this.startDate.setHours(0, 0, 0, 0);
        //set hours to end of day
        this.endDate.setHours(23, 59, 59, 999);
      }

      if (this.simpleDropDown) {
        this.wasSimpleDropDown = true;
        this.chips.push({
          label: 'Custom',
          value: 'Custom',
          visible: true,
        });
      }

      if (this.emitDateImmediately) {
        this.handleDateSelection();
      }
    }

    if (this.locationRequired) {
      this.locationTypeOption = this.locationType;

      // OLD

      this.entityIdSubscription$ = this.store
        .select(getSelectedSupplierId)
        .subscribe((entityId) => {
          this.entityId = entityId;
          if (this.initialLocationId) {
            this.setInitialLocation();
          }
        });

      // TODO IMPLEMENT STATE FOR LOCATIONS
      this.locationSearchSubscription$ = this.locationSearchSubject
        .asObservable()
        .pipe(
          debounceTime(500),
          switchMap((searchterm) => {
            this.loadingLocations = true;
            if (this.locationTypeOption === 'All') {
              return this.locationService.loadVehiclesAndLocations(
                this.entityId,
                this.currentPage,
                '25',
                searchterm
              );
            }
            if (this.locationTypeOption === 'Location') {
              return this.locationService.loadLocations(
                this.entityId,
                this.currentPage,
                '25',
                searchterm
              );
            } else if (this.locationTypeOption === 'Vehicle') {
              return this.vehicleService.loadVehicles(
                this.entityId,
                this.currentPage,
                '25',
                searchterm
              );
            }
          })
        )
        .subscribe(
          (res) => {
            const response = res as any;
            let items = [];
            if (this.locationTypeOption === 'All') {
              items = response.locations.concat(response.vehicles);
            } else {
              items = response.items;
            }

            //TODO this should be replaced by state
            this.locationSearchResults =
              this.locationSearchResults.concat(items);
            this.locationSearchResults =
              this.helperService.removeDuplicatesFromObjectArray(
                this.locationSearchResults,
                'id'
              );

            if (this.excludeLocationId)
              items = items.filter((x) => x.id !== this.excludeLocationId);

            this.locationFilteredResults = items;

            this.loadingLocations = false;
          },
          (error) => {
            console.log(error);
          }
        );

      if (this.singleLocation) {
        this.searchLocations();
      } else {
        this.searchMultipleLocations();
      }
    }
  }

  ngAfterViewInit(): void {
    this.multiSelectContainer = this.multiSelect?.containerViewChild;
  }

  //figure out if we have filtered results
  handleFilterLocations(filter = null, element = null) {
    this.filterValue = filter;
    this.showGlobalSearch = element._filteredOptions?.length ? false : true;
  }

  onPanelShow(element: any) {
    this.locationElement = element;
    element._filterValue = '';
    this.filterValue = '';
  }

  searchMultipleLocations(global = false) {
    this.locationSearchSubject.next(this.filterValue ?? '');
    this.searchGlobal = global;
    this.locationsFacade.dispatch(
      setLocationQuery({ query: this.filterValue })
    );
    this.locationsFacade.dispatch(loadLocations({}));
    this.vehiclesFacade.dispatch(setLocationQuery({ query: this.filterValue })); // use this location state instead of the search values
    this.vehiclesFacade.dispatch(loadVehicles({ query: this.filterValue }));
  }

  searchLocations(query = null) {
    this.filterValue = query;
    this.locationSearchSubject.next(this.filterValue ?? '');
  }

  loadAllLocation(event) {
    this.locationService
      .loadLocations(this.entityId)
      .subscribe((res) => (this.locationSearchResults = res.items));
  }

  async setInitialLocation() {
    const location = await this.locationService
      .loadLocation(this.entityId, this.initialLocationId)
      .toPromise();
    if (location) {
      this.selectedLocation = location;
    }
  }

  clearLocationSelection() {
    this.selectedLocations = [];
    this.locationSelectedChange.emit(this.selectedLocations);
  }

  handleLocationSelection(ev) {
    this.selectedLocations = Array.isArray(ev.value) ? ev.value : [ev.value];
    this.locationSelectedChange.emit(this.selectedLocations);
  }

  getMonday(d: Date) {
    // set constants
    const date = new Date(d),
      day = date.getDay(), // Sunday = 0...
      diff = date.getDate() - day + (day === 0 ? -6 : 1), // adjust when day is sunday
      dateDifference = new Date(date.setDate(diff));
    return dateDifference;
  }

  dateShortcut(val) {
    if (val && val !== 'null') {
      localStorage.setItem('dateShortcut', val);
    }

    this.selectedChip = val;
    this.endDate = new Date();

    const date = new Date();

    switch (val) {
      case 'startOfWeek':
        this.startDate = this.getMonday(date);
        console.log(this.startDate);
        break;

      case 'startOfLastWeek':
        this.startDate = this.getMonday(
          new Date(date.setDate(date.getDate() - 7))
        );
        this.endDate = new Date(date.setDate(this.startDate.getDate() + 6));
        break;

      case 'startOfMonth':
        this.startToEndMonths();
        break;

      case 'lastMonth':
        this.startToEndMonths(-1, -1);
        break;

      case 'threeMonths':
        this.startToEndMonths(-2);
        break;

      case 'yearToDate':
        this.startToEndMonths(0);
        break;

      case 'yesterday':
        this.startDate = new Date(date.setDate(date.getDate() - 1));
        this.endDate = new Date(this.startDate);
        break;
      case null:
        //all time
        this.startDate = null;
        this.endDate = null;
        break;

      default:
        if (typeof val === 'string' || val instanceof String) {
          this.startDate = new Date(date.setDate(date.getDate() + Number(val)));
        } else {
          //The received value is a number of days to subtract
          this.startDate = new Date(date.setDate(date.getDate() + val));
        }
    }

    if (this.calendarStart?.overlayVisible) {
      this.calendarStart.overlayVisible = false;
    }

    if (!this.singleDay && this.calendarEnd?.overlayVisible) {
      this.calendarEnd.overlayVisible = false;
    }

    this.handleDateSelection();
  }

  //TODO add input they can specify how many months back?
  startToEndMonths(
    startMonth = this.today.getMonth(),
    endMonth = this.today.getMonth()
  ) {
    if (startMonth < 0) startMonth = this.today.getMonth() + startMonth;
    if (endMonth < 0) endMonth = this.today.getMonth() + endMonth;
    this.startDate = new Date(this.today.getFullYear(), startMonth, 1);
    this.endDate = new Date(this.today.getFullYear(), endMonth + 1, 0);
  }

  calendarDateSelection() {
    this.selectedChip = null;
    this.handleDateSelection();
  }

  dropDownChange(ev) {
    if (ev?.value?.value === 'Custom') {
      this.singleDay = true;
      this.simpleDropDown = false;
    } else {
      this.dateShortcut(ev.value.value);
    }
  }

  inputDate(event) {
    if (event.inputType === 'deleteContentBackward') {
      this.clearDateEvent();
    }
  }

  clearDateEvent() {
    if (this.wasSimpleDropDown) {
      this.singleDay = false;
      this.simpleDropDown = true;
    }
    this.startDate = null;
    this.endDate = null;

    this.datesSelected.emit(['', '', 'No Date']);
  }

  handleDateSelection() {
    let dateString = '';

    if (!this.startDate && !this.showTime && !this.singleDay) {
      const endDate = new Date(this.today.setHours(24, 0, 0, 0) - 1);
      this.datesSelected.emit([
        null,
        endDate,
        'Everything up to end of selected Date',
      ]);
      return;
    }

    if (this.singleDay) {
      if (!this.startDate)
        this.startDate = new Date(this.today.setHours(0, 0, 0, 0));
      this.endDate = new Date(this.startDate);
    } else if (this.startDate && !this.endDate) {
      this.endDate = new Date(this.today);
    }

    if (!this.showTime) {
      // Set hours to start and end of dates
      this.startDate.setHours(0, 0, 0, 0);
      this.endDate.setHours(23, 59, 59, 999);
    }

    if (this.forceEndOfDay) {
      this.startDate.setHours(23, 59, 59, 999);
      this.endDate.setHours(23, 59, 59, 999);
    }

    if (this.selectedDate?.label.indexOf('Month') > -1) {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];

      dateString = `${
        monthNames[this.startDate.getMonth()]
      } ${this.startDate.getFullYear()}`;
    } else if (this.startDate?.toString() === this.endDate?.toString()) {
      dateString = this.helperService.friendlyDate(
        this.startDate,
        false,
        false,
        true
      );
    } else {
      const startDateString = this.helperService.friendlyDate(
        this.startDate,
        false,
        true,
        true
      );
      const endDateString = this.helperService.friendlyDate(
        this.endDate,
        false,
        true,
        true
      );
      dateString = `From: ${startDateString} To: ${endDateString}`;
    }

    const toGMTString = (date) =>
      new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
    const gmtStartDate = toGMTString(this.startDate);
    const gmtEndDate = toGMTString(this.endDate);

    if (gmtStartDate === gmtEndDate && !this.singleDay) {
      console.log('Date is Identical');
      this.startDate.setHours(0, 0, 0, 0);
      this.endDate.setHours(23, 59, 59, 999);
    }

    this.datesSelected.emit([gmtStartDate, gmtEndDate, dateString]);
  }

  updateFilters(ev = null) {
    if (ev) {
      this.filterValue = ev.filter;
      //Checking if we have results for the filter...
      if (this.multiSelectContainer && !this.loadingLocations) {
        const multiContainer =
          this.multiSelectContainer.nativeElement.querySelector(
            '.p-multiselect-items'
          );
        const emptyMessage = multiContainer.querySelector(
          '.p-multiselect-empty-message'
        );
        if (emptyMessage) {
          console.log('The multiselect is empty.');
          this.searchAllLocations();
        } else {
          console.log('The multiselect is not empty.');
        }
      }
    }
    this.locationFilteredResults = this.locationSearchResults.filter(
      (x) => x.id !== this.excludeLocationId
    );
  }

  handleFilter(event) {
    this.filterValue = event.filter;
  }

  searchAllLocations() {
    this.locationSearchSubject.next(this.filterValue ?? '');
  }

  ngOnDestroy() {
    this.entityIdSubscription$?.unsubscribe();
    this.locationSearchSubscription$?.unsubscribe();
    this.locationVehicleSubscription$?.unsubscribe();
  }
}
